import {
  formatDataForSelectDropdown,
  formatFlatArrayToSelectDropdown
} from '@nile/components';
import { cloneDeep, isEmpty } from 'lodash';
import { TextCell } from '@nile/rmm/src/modules/scripts/components/cells';
import DateTimeCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/date-time-cell';
import ResetCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/reset-cell';
import AlertActionsCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/alert-actions';
import ActionTextCell from '@nile/rmm/src/modules/asset-management/components/sub-pages/more-info-text-cell';
import AutoInfoCell from '@nile/rmm/src/modules/asset-management/components/sub-pages/auto-info-text-cell';
import SeverityCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/severity-cell';
import StatusCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/status-cell';
import StatusTagCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/status-tag-cell';
import OccurrenceCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/occurrence-cell';
import MessageCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/message-cell';
import { GET_ALERT_SEVERITY_LIST } from '@nile/banyan/src/Graphql/Query/policy';
import { GET_WORKSTATION_LIST } from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { GET_UNIQUE_POLICY_NAMES } from '@nile/rmm/src/modules/alert-management/Graphql/alert';
import {
  apiResolver,
  GET_CLIENTS,
  TicketClient,
  AccountClient,
  RMMClient
} from '@nile/graphql';
import { GET_CLIENT_SITE_LIST_FOR_DROPDOWN } from '@nile/banyan/src/Graphql/Mutations/site';
import AssetTypeCell from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-type-cell';

// *************************** LIST HELPER FUNCTIONS *************************

const columnsConfig = {
  minWidth: 30,
  maxWidth: 500,
  width: 300,
  TEXT: TextCell,
  AssetType: AssetTypeCell,
  RESET: ResetCell,
  DATETIME: DateTimeCell,
  ACTIONS: AlertActionsCell,
  SEVERITY: SeverityCell,
  STATUS: StatusCell,
  ACTIONTEXT: ActionTextCell,
  STATUSTAGCELL: StatusTagCell,
  AUTO_TEXT_CELL: AutoInfoCell,
  OCCURRENCE: OccurrenceCell,
  MESSAGE: MessageCell
};

const processFilters = (conditions) => {
  const data = conditions.operands.map((operand) => {
    if (operand?.operands) {
      processFilters(operand);
    }
    switch (operand.attribute) {
      case 'client.name':
        operand.attribute = 'asset.client.name';
        break;
      case 'site.name':
        operand.attribute = 'asset.site.name';
        break;
      default:
        break;
    }
    return operand;
  });
  return data;
};

const getProcessedFilters = (filters) => {
  let processedFilters = cloneDeep(filters);

  if (!isEmpty(processedFilters)) {
    processedFilters = processFilters(filters);
  }

  return !isEmpty(processedFilters)
    ? {
        joinOperator: processedFilters[0].joinOperator,
        operands: processedFilters
      }
    : null;
};

const getAttributeName = (column) => {
  switch (column?.name) {
    case 'asset.name':
      return 'asset.name';
    case 'client.name':
      return 'asset.client.name';
    case 'site.name':
      return 'asset.site.name';
    case 'policy.name':
      return 'policy.name';
    default:
      return column?.name;
  }
};

const getColumnWidth = (colName, isMinWidth = false) => {
  switch (colName) {
    case 'message':
    case 'asset.name':
    case 'description':
      return 300;
    case 'reset':
      return 100;
    case 'policy.name':
      return 200;
    case 'status':
      return 225;
    case 'occurrenceCount':
      return 140;
    default:
      return isMinWidth ? 50 : 180;
  }
};

const getType = (col) => {
  switch (col.name) {
    case 'asset.name':
      return 'AssetType';
    case 'reset':
      return 'RESET';
    case 'createdTime':
      return 'DATETIME';
    case 'resolvedTime':
      return 'DATETIME';
    case 'severity':
      return 'SEVERITY';
    case 'status':
      return 'STATUS';
    case 'policy.name':
      return 'ACTIONTEXT';
    case 'description':
      return 'AUTO_TEXT_CELL';
    case 'tag':
      return 'STATUSTAGCELL';
    case 'occurrenceCount':
      return 'OCCURRENCE';
    case 'message':
      return 'MESSAGE';
    default:
      return 'TEXT';
  }
};

const getAccessor = (col) => {
  switch (col.name) {
    case 'asset.name':
      return 'asset.name';
    case 'client.name':
      return 'asset.client.name';
    case 'site.name':
      return 'asset.site.name';
    case 'policy.name':
      return 'policy.name';
    default:
      return col.name;
  }
};

const getMeta = (col) => {
  switch (col.name) {
    case 'policy.name':
      return {
        title: 'Description',
        accessor: 'description',
        icon: 'Bolt'
      };
    default:
      break;
  }
};

const getHomeWidgetMeta = (col) => {
  switch (col.name) {
    case 'policy.name':
      return {
        title: 'Description',
        accessor: 'description',
        icon: 'Bolt'
      };
    case 'status':
      return {
        noAction: true
      };
    default:
      break;
  }
};

const getTableVariables = (col) => {
  switch (col.name) {
    case 'client.name':
      return { listInfo: {} };
    default:
      return null;
  }
};

const checkDynamicQuery = (f) => {
  switch (f.name) {
    case 'client.name':
    case 'policy.name':
    case 'severity':
    case 'status':
    case 'site.name':
      return true;
    default:
      return false;
  }
};

const getDynamicQuery = (f) => {
  const resolverModule = 'Ticket';
  let client = TicketClient;
  let query = apiResolver[resolverModule][f.name]?.api;
  switch (f.name) {
    case 'client.name':
      client = AccountClient;
      query = GET_CLIENTS;
      return client.query({
        query: query,
        variables: {
          listInfo: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        }
      });
    case 'site.name':
      client = AccountClient;
      query = GET_CLIENT_SITE_LIST_FOR_DROPDOWN;
      return client.query({
        query: query,
        variables: {
          input: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        }
      });
    case 'policy.name':
      return RMMClient.query({
        query: GET_UNIQUE_POLICY_NAMES,
        variables: {
          pattern: null
        }
      });
    case 'severity':
      return RMMClient.query({
        query: GET_ALERT_SEVERITY_LIST,
        variables: {}
      });
    case 'status':
      return {
        data: [
          {
            label: 'Open',
            value: 'Open'
          },
          {
            label: 'Resolved',
            value: 'Resolved'
          }
        ]
      };
    default:
      query = apiResolver[resolverModule][f.name]?.api;
      client = apiResolver[resolverModule][f.name]?.client;
      return client.query({
        query: query
      });
  }
};

const generateOptions = (f, data) => {
  let allValues = [];
  if (f.isCustom) {
    allValues = formatDataForSelectDropdown(
      data.getCustomFieldByName.options || []
    );
    return allValues;
  }
  switch (f.name) {
    case 'client.name':
      allValues = formatDataForSelectDropdown(data.getClientList.clients || []);
      break;
    case 'site.name':
      allValues = formatDataForSelectDropdown(
        data.getClientSiteList.sites || []
      );
      break;
    case 'policy.name':
      allValues = formatFlatArrayToSelectDropdown(
        data.getUniquePolicyNames || []
      );
      break;
    case 'severity':
      allValues = formatDataForSelectDropdown(data.getAlertSeverityTypes || []);
      break;
    case 'status':
      allValues = allValues.concat(data);
      break;
    default:
      allValues = [];
      break;
  }
  return allValues;
};

/**
 * @description returns the graphql schema based on the attribute
 * @param {object} - property
 * @return {object}
 * @private
 */
function getQuery(property) {
  switch (property.name) {
    case 'asset.name':
      return GET_WORKSTATION_LIST;
    default:
      break;
  }
}

/**
 * @description returns the accessKey based on the attribute
 * @param {object} - property
 * @return {string}
 * @private
 */
function getNameKey(property) {
  switch (property.name) {
    case 'asset.name':
      return 'getWorkStationList';
    default:
      return 'getRequesterList';
  }
}

/**
 * @description There are few attributes for which we don't want to fetch the values from the server on selecting the attribute
 * rather, we want to fetch on typing atleast 2 character, that is when this function gets triggered (in the attribute object, we have 'autocomplete' property as true)
 * @param {object} - client
 * @param {object} - query
 * @param {object} - params
 * @return {Array | Array<object>}
 * @public
 */
const fetchValues = async (client, query, params) => {
  const attribute = 'name';
  const nameKey = getNameKey(params.property);

  const variables = {
    listInfo: {
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: attribute,
            operator: 'contains',
            value: params.inputValue
          }
        ]
      }
    }
  };

  const res = await client.query({
    query: query,
    variables
  });

  let allOptions = [];

  if (nameKey === 'getWorkStationList') {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey].assets.map((el) => {
          return {
            name: `${el[attribute]}`,
            value: `${el[attribute]}`,
            label: `${el[attribute]}`
          };
        })
      : [];
  } else {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey].map((el) => {
          return {
            name: `${el[attribute]}`,
            value: `${el[attribute]}`,
            label: `${el[attribute]}`
          };
        })
      : [];
  }

  return allOptions;
};

/**
 * @description returns RMMClient and query based on the selected attribute
 * @param {object} - property
 * @return {object}
 * @public
 */
const getAsyncOptions = (property) => {
  return { client: RMMClient, query: getQuery(property) };
};

const getRuleAttribute = (f) => {
  switch (f.name) {
    case 'client.name':
      return 'asset.client.name';
    case 'site.name':
      return 'asset.site.name';
    default:
      return f.name;
  }
};

const checkCustomAttribute = (attr, field) => {
  const ruleAttribute = getRuleAttribute(field);
  return attr === ruleAttribute;
};

export {
  getProcessedFilters,
  getAttributeName,
  getColumnWidth,
  getType,
  getAccessor,
  columnsConfig,
  getMeta,
  getTableVariables,
  checkDynamicQuery,
  getDynamicQuery,
  generateOptions,
  getQuery,
  fetchValues,
  getAsyncOptions,
  getHomeWidgetMeta,
  getRuleAttribute,
  checkCustomAttribute
};
